import { zodResolver } from '@hookform/resolvers/zod';
import { useHeaderHeight } from '@react-navigation/elements';
import { useQueryClient } from '@tanstack/react-query';
import { router, usePathname } from 'expo-router';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { KeyboardAvoidingView, Platform, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import { useCustomer } from '@/api/customers/use-customer';
import { useUpdateCustomer } from '@/api/customers/use-update-customer';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { isDesktop } from '@/core';
import { Button, showErrorMessage, Text } from '@/ui';
import { ControlledInput } from '@/ui/input/index';
import { Loader } from '@/ui/loader';

type NameFormProps = {
  firstName: string;
  lastName: string;
};

const nameFormSchema = z.object({
  firstName: z.string().min(2, 'Minimum 2 characters'),
  lastName: z.string().min(2, 'Minimum 2 characters'),
});

export type NameFormType = z.infer<typeof nameFormSchema>;

export default function ProfileName() {
  const { styles, theme } = useStyles(stylesheet);
  const pathname = usePathname();
  const headerHeight = useHeaderHeight();
  const queryClient = useQueryClient();

  const { mutate: updateCustomer, isPending: isUpdatingCustomer } =
    useUpdateCustomer();

  const { data: customer } = useCustomer();

  const {
    handleSubmit,
    control,
    trigger,
    setValue,
    getValues,
    formState: { isValid },
  } = useForm<NameFormType>({
    defaultValues: {
      firstName: customer?.firstname,
      lastName: customer?.lastname,
    },
    resolver: zodResolver(nameFormSchema),
  });

  React.useEffect(() => {
    if (customer?.firstname) {
      setValue('firstName', customer.firstname);
    }

    if (customer?.lastname) {
      setValue('lastName', customer.lastname);
    }
  }, [customer?.firstname, customer?.lastname]);

  const handleContinue = (data: NameFormProps) => {
    updateCustomer(
      {
        firstName: data.firstName,
        lastName: data.lastName,
      },
      {
        onSuccess: (d) => {
          queryClient.setQueryData(['customers/me'], d.data);

          Toast.show({
            type: 'success',
            text1: 'Name updated successfully',
          });
        },
        onError: (e) => {
          showErrorMessage(e.message);
        },
      }
    );
  };

  if (isUpdatingCustomer) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={{
        flexGrow: 1,
        backgroundColor: theme.colors.background_1,
        alignItems: 'center',
      }}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={headerHeight}
    >
      <View style={styles.container}>
        {isDesktop() ? (
          <View style={{ gap: 16 }}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                if (pathname.includes('/partner')) {
                  router.navigate('/partner/more/profile');
                  return;
                }

                router.navigate('/account/profile');
              }}
            />

            <Text type="largeTitle">Name</Text>
          </View>
        ) : null}

        <View style={styles.contentContainer}>
          <View style={{ gap: 16 }}>
            <ControlledInput
              control={control}
              name="firstName"
              label="First name"
              onBlur={() => {
                trigger('firstName');
              }}
              onChangeText={(text) => {
                trigger('firstName');
                setValue('firstName', text);
              }}
              autoCapitalize="words"
            />

            <ControlledInput
              control={control}
              name="lastName"
              label="Last name"
              onBlur={() => {
                trigger('lastName');
              }}
              onChangeText={(text) => {
                trigger('lastName');
                setValue('lastName', text);
              }}
              autoCapitalize="words"
            />
          </View>

          <View>
            <Button
              type="primary"
              tx="profileName.update"
              onPress={handleSubmit(handleContinue)}
              disabled={
                !isValid ||
                (customer?.firstname === getValues().firstName &&
                  customer?.lastname === getValues().lastName)
              }
            />
          </View>
        </View>
      </View>
    </KeyboardAvoidingView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
    gap: 16,
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 800,
    },
    width: {
      xs: '100%',
      md: 600,
    },
  },
  contentContainer: {
    flexGrow: 1,
    gap: 24,
    justifyContent: {
      xs: 'space-between',
      md: 'flex-start',
    },
  },
}));
